import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { EmptyState, Skeleton, PageContainer, Card, CardHeader, Button } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import { getSimulations, executeSimulation, getDashboard } from '../api/api';

const STEP_KEY_BY_TYPE_AND_ORDER = {
  PHISHING: { 1: 'activityStepPhishing1', 2: 'activityStepPhishing2', 3: 'activityStepPhishing3', 4: 'activityStepPhishing4' },
  SOCIAL_ENGINEERING: { 1: 'activityStepSE1', 2: 'activityStepSE2', 3: 'activityStepSE3', 4: 'activityStepSE4' },
  BRUTE_FORCE: { 1: 'activityStepBrute1', 2: 'activityStepBrute2', 3: 'activityStepBrute3', 4: 'activityStepBrute4' },
};

function getSimulationCards(t) {
  return [
    { type: 'PHISHING', labelKey: 'phishing', descKey: 'phishingDesc', hasIntro: true, riskCategory: 'phishing' },
    { type: 'SOCIAL_ENGINEERING', labelKey: 'socialEngineering', descKey: 'socialEngineeringDesc', hasIntro: true, riskCategory: 'socialEngineering' },
    { type: 'BRUTE_FORCE', labelKey: 'bruteForce', descKey: 'bruteForceDesc', hasIntro: true, riskCategory: 'authority' },
  ];
}

const RISK_COLORS = {
  LOW: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30',
  HIGH: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30',
};

function parseApiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return new Date(value);

  const hasExplicitTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(value);
  if (hasExplicitTimezone) return new Date(value);

  const localCandidate = new Date(value);
  const utcCandidate = new Date(`${value}Z`);

  if (Number.isNaN(localCandidate.getTime())) return utcCandidate;
  if (Number.isNaN(utcCandidate.getTime())) return localCandidate;

  const deltaMs = localCandidate.getTime() - Date.now();
  if (deltaMs > 2 * 60 * 60 * 1000) return utcCandidate;
  return localCandidate;
}

function formatRelativeDate(dateStr, t, locale = 'pt-BR') {
  if (!dateStr || !t) return null;
  const d = parseApiDate(dateStr);
  if (!d || Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t.today;
  if (diffDays === 1) return t.yesterday;
  if (diffDays < 7) return t.daysAgo.replace('{n}', diffDays);
  return d.toLocaleDateString(locale);
}

function formatTime(dateStr, locale = 'pt-BR') {
  if (!dateStr) return '';
  const parsed = parseApiDate(dateStr);
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getStepDescription(t, simulationType, step) {
  if (simulationType === 'BRUTE_FORCE') return step?.description || '';
  const key = STEP_KEY_BY_TYPE_AND_ORDER[simulationType]?.[step?.stepOrder];
  return (key && t[key]) || step?.description || '';
}

function formatCrackDuration(seconds, t) {
  if (seconds == null) return '—';
  if (seconds <= 1) return t.lessThanSecond;
  if (seconds >= 30 * 24 * 3600) return t.moreThanMonth;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

function getBruteForceOutcomeMeta(outcome, t) {
  switch (outcome) {
    case 'BLOCKED':
      return { className: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30', label: t.bruteForceOutcomeBlocked };
    case 'RATE_LIMITED':
      return { className: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30', label: t.bruteForceOutcomeRateLimited };
    case 'SUCCESS':
      return { className: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30', label: t.bruteForceOutcomeSuccess };
    default:
      return { className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30', label: t.bruteForceOutcomeIndeterminate };
  }
}

function getSimulationStatus(sim, t) {
  if (!sim || !t) return null;
  if (sim.simulationType === 'PHISHING') return { ok: true, text: t.phishingSimComplete };
  if (sim.simulationType === 'SOCIAL_ENGINEERING') return { ok: true, text: t.socialEngineeringSimComplete };
  if (sim.simulationType === 'BRUTE_FORCE') return { ok: true, text: t.bruteForceSimComplete };
  return { ok: true, text: t.simulationComplete };
}

function computeRiskFromAwareness(awarenessScore) {
  if (awarenessScore == null) return 'LOW';
  if (awarenessScore >= 80) return 'LOW';
  if (awarenessScore >= 50) return 'MEDIUM';
  return 'HIGH';
}

function computeHumanRiskScore(dashboard) {
  const se = dashboard?.socialEngineeringAwarenessScore;
  if (se == null) return null;
  return Math.round(100 - se);
}

function cardMatchesQuery(card, q, label, description) {
  if (!q || !q.trim()) return false;
  const lower = q.trim().toLowerCase();
  const l = (label || '').toLowerCase();
  const d = (description || '').toLowerCase();
  const type = (card.type || '').toLowerCase();
  const risk = (card.riskCategory || '').toLowerCase();
  return (
    l.includes(lower) || d.includes(lower) || type.includes(lower) || risk.includes(lower) ||
    (lower === 'phishing' && type.includes('phishing')) ||
    (lower === 'brute' && type.includes('brute')) ||
    ((lower === 'engenharia social' || lower === 'social engineering') && type.includes('social'))
  );
}

export default function Simulations() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const locale = (language === 'en-US' || language === 'en') ? 'en-US' : 'pt-BR';
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const cardRefs = useRef({});
  const detailsRef = useRef(null);
  const SIMULATION_CARDS = useMemo(() => getSimulationCards(t), [t]);
  const RISK_LABELS = { LOW: t.low, MEDIUM: t.medium, HIGH: t.high };
  const hasScrolledToMatch = useRef(false);

  const [simulations, setSimulations] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const [error, setError] = useState('');
  const [selectedSim, setSelectedSim] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([getSimulations(organizationId), getDashboard(organizationId)])
      .then(([sims, dash]) => {
        setSimulations(sims);
        setDashboard(dash);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!location.state?.justExecutedSimulation) return;
    setSelectedSim(location.state.justExecutedSimulation);
    navigate(location.pathname + location.search, { replace: true });
  }, [location.state, navigate, location.pathname, location.search]);

  // Scroll to and highlight first matching card when arriving via search
  useEffect(() => {
    if (!searchQuery.trim() || loading || hasScrolledToMatch.current) return;
    const match = SIMULATION_CARDS.find((c) => cardMatchesQuery(c, searchQuery, t[c.labelKey], t[c.descKey]));
    if (!match) return;
    const ref = cardRefs.current[match.type];
    if (ref) {
      hasScrolledToMatch.current = true;
      setTimeout(() => {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [searchQuery, loading, SIMULATION_CARDS, t]);

  useEffect(() => {
    if (!selectedSim || !detailsRef.current) return;
    detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedSim]);

  const handleCardClick = (card) => {
    setError('');
    if (card.hasIntro) {
      if (card.type === 'PHISHING') {
        navigate(`/org/${organizationId}/simulations/phishing-intro`);
      } else if (card.type === 'SOCIAL_ENGINEERING') {
        navigate(`/org/${organizationId}/simulations/social-engineering-intro`);
      } else if (card.type === 'BRUTE_FORCE') {
        navigate(`/org/${organizationId}/simulations/brute-force-lab`);
      }
    } else {
      handleExecute(card.type);
    }
  };

  const handleExecute = async (type) => {
    setExecuting(type);
    setError('');
    try {
      const result = await executeSimulation(organizationId, type);
      setSelectedSim(result);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(null);
    }
  };

  const getSimLabel = (type) => {
    const card = SIMULATION_CARDS.find((s) => s.type === type);
    return card ? t[card.labelKey] : type;
  };

  const getLastSimulationByType = (type) =>
    simulations.find((s) => s.simulationType === type);

  const humanRiskScore = computeHumanRiskScore(dashboard);
  const seAwareness = dashboard?.socialEngineeringAwarenessScore;
  const phishingRisk = computeRiskFromAwareness(seAwareness);
  const socialEngineeringRisk = computeRiskFromAwareness(seAwareness);
  const authorityRisk = computeRiskFromAwareness(seAwareness);
  const bruteForceOutcomeMeta = selectedSim?.simulationType === 'BRUTE_FORCE'
    ? getBruteForceOutcomeMeta(selectedSim.bruteForceOutcome, t)
    : null;
  const selectedSimExecutedAt = parseApiDate(selectedSim?.executedAt);

  return (
    <Layout>
      <PageContainer>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 tracking-tight">
            {t.attackSimulations}
          </h1>
          <p className="text-[#475569] dark:text-slate-400 mt-1">
            {t.simulationsSubtitle}
          </p>
        </div>

        {error && (
          <div className="rounded-lg bg-red-100 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 px-4 py-3 text-red-800 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Visão Geral de Risco de Segurança */}
        <Card className="overflow-hidden">
          <CardHeader title={t.riskOverview} subtitle={t.riskOverviewSubtitle} />
          <div className="p-6 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {t.humanRiskScore}
                </span>
                <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                  {humanRiskScore != null ? `${humanRiskScore} / 100` : '—'}
                </span>
              </div>
              <div className="h-2.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${humanRiskScore ?? 0}%`,
                    backgroundColor:
                      (humanRiskScore ?? 0) <= 30
                        ? '#10b981'
                        : (humanRiskScore ?? 0) <= 60
                        ? '#f59e0b'
                        : '#ef4444',
                  }}
                />
              </div>
              {humanRiskScore == null && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                  {t.runSocialEngineeringForScore}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  {t.phishingRisk}
                </p>
                <span
                  className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold border ${RISK_COLORS[phishingRisk]}`}
                >
                  {RISK_LABELS[phishingRisk]}
                </span>
              </div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  {t.socialEngineeringRisk}
                </p>
                <span
                  className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold border ${RISK_COLORS[socialEngineeringRisk]}`}
                >
                  {RISK_LABELS[socialEngineeringRisk]}
                </span>
              </div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  {t.authorityFraudRisk}
                </p>
                <span
                  className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold border ${RISK_COLORS[authorityRisk]}`}
                >
                  {RISK_LABELS[authorityRisk]}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Simulation Cards */}
        <section>
          <h2 className="text-base font-semibold text-[#0f172a] dark:text-slate-100 mb-4">
            {t.availableSimulations}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SIMULATION_CARDS.map((card) => {
              const lastSim = getLastSimulationByType(card.type);
              const status = getSimulationStatus(lastSim || { simulationType: card.type }, t);
              const isHighlighted = searchQuery.trim() && cardMatchesQuery(card, searchQuery, t[card.labelKey], t[card.descKey]);
              return (
                <div key={card.type} ref={(el) => { cardRefs.current[card.type] = el; }}>
                  <Card
                    className={`overflow-hidden transition-all duration-500 h-full ${
                      isHighlighted
                        ? 'border-[#0ea5e9] ring-2 ring-[#0ea5e9]/30'
                        : ''
                    }`}
                    hover
                  >
                  <div className="p-6 h-full flex flex-col">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-200 mb-2">
                      {t[card.labelKey]}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-4 leading-relaxed min-h-[48px]">
                      {t[card.descKey]}
                    </p>
                    {status ? (
                      <div className="space-y-2 mb-5 min-h-[52px]">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {t.lastRun}: {lastSim ? formatRelativeDate(lastSim.executedAt, t, locale) : '—'}
                        </p>
                        <div className={`flex items-center gap-2 text-xs ${lastSim ? 'text-green-600 dark:text-green-500' : 'text-slate-500 dark:text-slate-400'}`}>
                          <span>✔</span>
                          <span>{lastSim ? status.text : t.noSimulationsYet}</span>
                        </div>
                      </div>
                    ) : null}
                    <Button
                      onClick={() => handleCardClick(card)}
                      disabled={executing !== null}
                      loading={executing === card.type}
                      variant="primary"
                      className="w-full py-3 mt-auto"
                    >
                      {executing === card.type ? t.starting : t.startSimulation}
                    </Button>
                  </div>
                </Card>
                </div>
              );
            })}
          </div>
        </section>

        {/* Selected Simulation Detail - Timeline */}
        {selectedSim && (
          <div ref={detailsRef}>
          <Card className="overflow-hidden">
            <CardHeader
              title={t.simulationDetails}
              subtitle={`${t.simulationOf} ${getSimLabel(selectedSim.simulationType)} • ${t.executedAt} ${selectedSimExecutedAt ? selectedSimExecutedAt.toLocaleString(locale) : ''} ${t.by} ${selectedSim.executedByName}`}
            />
            <div className="p-6">
              {selectedSim.simulationType === 'BRUTE_FORCE' && (
                <div className="mb-6 p-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <h3 className="text-base font-semibold text-[#0f172a] dark:text-slate-100">
                      {t.bruteForceResultTitle}
                    </h3>
                    <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold border ${bruteForceOutcomeMeta?.className}`}>
                      {bruteForceOutcomeMeta?.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    {selectedSim.policySummary || t.bruteForceDetailsDefaultSummary}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.simulatedAttempts}</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                        {selectedSim.simulatedAttemptCount ?? '—'}
                      </p>
                    </div>
                    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.blockThreshold}</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                        {selectedSim.thresholdBeforeBlock ?? '—'}
                      </p>
                    </div>
                    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.passwordStrengthLevel}</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                        {selectedSim.passwordStrengthLevel ? t[`strength${selectedSim.passwordStrengthLevel}`] || selectedSim.passwordStrengthLevel : '—'}
                      </p>
                    </div>
                    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.passwordEntropyBits}</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                        {selectedSim.passwordEntropyBits != null ? selectedSim.passwordEntropyBits : '—'}
                      </p>
                    </div>
                    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.estimatedCrackTime}</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                        {formatCrackDuration(selectedSim.estimatedCrackSeconds, t)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <h3 className="text-sm font-semibold text-[#0f172a] dark:text-slate-200 mb-4">
                {t.timeline}
              </h3>
              <div className="relative">
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200 dark:bg-slate-600 rounded-full" />
                <ul className="space-y-0">
                  {selectedSim.steps?.map((step, i) => {
                    const baseTime = selectedSimExecutedAt?.getTime();
                    if (baseTime == null || Number.isNaN(baseTime)) return null;
                    const stepTime = new Date(baseTime + (i + 1) * 60000);
                    return (
                      <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
                        <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-[#0ea5e9]/10 border-2 border-[#0ea5e9]/40 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-[#0ea5e9]">
                            {i + 1}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0 pt-0.5">
                          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-0.5">
                            {formatTime(stepTime, locale)}
                          </p>
                          <p className="text-slate-700 dark:text-slate-200 font-medium">
                            {getStepDescription(t, selectedSim.simulationType, step)}
                          </p>
                          {step.impact && (
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              {step.impact}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {selectedSim.simulationType === 'BRUTE_FORCE' && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-4">
                  {t.timelineTimesIllustrative}
                </p>
              )}
            </div>
          </Card>
          </div>
        )}

        {/* History Table */}
        <Card className="overflow-hidden">
          <CardHeader title={t.simulationHistory} subtitle={t.simulationHistorySubtitle} />
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : simulations.length === 0 ? (
              <EmptyState
                icon={<svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
                title={t.noSimulationsYet}
                description={t.runFirstSimulation}
                action={<Button variant="primary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>{t.viewSimulations}</Button>}
              />
            ) : (
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t.type}
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t.date}
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t.executedBy}
                    </th>
                    <th className="text-right py-4 px-6 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t.action}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {simulations.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <td className="py-4 px-6 font-medium text-slate-700 dark:text-slate-200">
                        {getSimLabel(s.simulationType)}
                      </td>
                      <td className="py-4 px-6 text-slate-500 dark:text-slate-400 text-sm">
                        {(() => {
                          const d = parseApiDate(s.executedAt);
                          return d && !Number.isNaN(d.getTime()) ? d.toLocaleString(locale) : '';
                        })()}
                      </td>
                      <td className="py-4 px-6 text-slate-500 dark:text-slate-400 text-sm">
                        {s.executedByName}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => setSelectedSim(s)}
                          className="text-[#0ea5e9] hover:text-[#0284c7] text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0ea5e9]/40 focus:ring-offset-1 rounded px-2 py-1 transition duration-200"
                        >
                          {t.viewDetails}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </PageContainer>
    </Layout>
  );
}
